import { themeAtom } from "../../shared/atoms";
import { VoyageInfo } from "../models/voyage.interfaces";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type VoyageDetailsProps = {
  voyageInfo: VoyageInfo[];
};

// TODO RENAME
export function VoyageDetails({ voyageInfo }: Readonly<VoyageDetailsProps>) {
  const theme = useAtomValue(themeAtom);
  const details = voyageInfo?.[0];
  const labelClassName = twMerge("text-xs font-normal", theme.vesselDetails.fields);
  const valueClassName = twMerge("mt-1 text-sm font-normal", theme.home.tailwind.textDefault);
  const secondaryValueClassName = twMerge("mt-1 text-sm font-normal", theme.vesselDetails.info);

  const formatDate = (dateInput?: Date | string) => {
    if (!dateInput) return "-";
    const date = new Date(dateInput);
    if (Number.isNaN(date.getTime())) return "-";

    const formatted = date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    const parts = formatted.split(" ");
    if (parts.length !== 3) return formatted;
    return `${parts[0]} ${parts[1]}, ${parts[2]}`;
  };

  return (
    <div
      className={twMerge(
        "w-full rounded-lg border",
        theme.home.tailwind.panelBackground,
        theme.vesselDetails.cardBackground,
        theme.uwhcAssistant.chatUi.panelBorder,
      )}
    >
      <div
        className={twMerge(
          "rounded-t-lg border-b px-6 py-4",
          theme.opsKnowledgeAssistant.chatUi.headerBg,
          theme.uwhcAssistant.chatUi.panelBorder,
        )}
      >
        <div className={twMerge("text-lg font-bold", theme.home.tailwind.textDefault)}>Voyage Details</div>
      </div>

      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={twMerge(
              "rounded-[10px] border bg-green px-3 py-1 text-sm font-medium leading-5",
              theme.vesselDetails.statusBadgeText,
              theme.uwhcAssistant.tableBorder,
            )}
          >
            {details?.VOYAGE_STATUS_CODE ?? "-"}
          </span>
          <span
            className={twMerge(
              "rounded-[10px] border px-3 py-1 text-sm font-medium leading-5",
              theme.uwhcAssistant.tableBorder,
              theme.uwhcAssistant.inputBg,
              theme.home.tailwind.textDefault,
            )}
          >
            {details?.OPR_TYPE ?? "-"}
          </span>
        </div>

        <div>
          <div className={labelClassName}>Vessel</div>
          <div className={valueClassName}>{details?.VESSEL_NAME ?? "-"}</div>
        </div>

        <div>
          <div className={labelClassName}>IMO</div>
          <div className={secondaryValueClassName}>
            {details?.IMO_NUMBER ?? "-"} <span className="px-2">&bull;</span> {details?.VESSEL_TYPE ?? "-"}
          </div>
        </div>

        <div>
          <div className={labelClassName}>Timecharter</div>
          <div className={twMerge("mt-1 text-sm font-normal", theme.vesselDetails.tcLinkText)}>
            <Link className="cursor-pointer" to={`/voyage/${details?.TC_CODE ?? ""}`}>
              {details?.TC_CODE ?? "-"}
            </Link>
          </div>
        </div>

        <div>
          <div className={labelClassName}>Voyage period</div>
          <div className={valueClassName}>
            <span className={theme.vesselDetails.info}>From:</span> {formatDate(details?.COMMENCE_DATE_GMT)}
            <span className={twMerge("px-2", theme.vesselDetails.info)}>&rarr;</span>
            <span className={theme.vesselDetails.info}>To:</span> {formatDate(details?.COMPLETED_DATE_GMT)}
          </div>
        </div>

        <div className={twMerge("h-px w-full", theme.vesselDetails.divider)} />

        <div className="space-y-4">
          <div>
            <div className={twMerge("flex items-center gap-2 text-xs font-normal", theme.vesselDetails.fields)}>
              <UserGroupIcon className="h-4 w-4" />
              <span>Trader:</span>
            </div>
            <p className={twMerge("mt-1 text-sm font-semibold", theme.home.tailwind.textDefault)}>
              {details?.CHTR_SPECIALIST ?? "-"}
            </p>
          </div>
          <div>
            <div className={twMerge("flex items-center gap-2 text-xs font-normal", theme.vesselDetails.fields)}>
              <UserGroupIcon className="h-4 w-4" />
              <span>Operation Coordinator:</span>
            </div>
            <p className={twMerge("mt-1 text-sm font-semibold", theme.home.tailwind.textDefault)}>
              {details?.OPS_COORDINATOR ?? "-"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
